import { connectClinicToGroup, createTcGroup, deleteTcGroup, disconnectClinicFromGroup, getConnectedClinics, getConsultantProfile, getPendingMemberIds, getTcGroupDetail, getTcGroupInvitations, getTcGroupMembers, getTcGroups, patchConnectionPreferences, provisionTcGroupMember, removeTcGroupMember, revokePendingMember, updateClinicConnection, updateTcGroup, updateTcGroupMemberRole, } from '@/lib/api';
import { ApiError, getActiveRegion } from '@/lib/api/client';
import { queryClient } from '@/lib/queryClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useEffectiveClinics } from './use-current-user';
import { useToast } from './use-toast';
export function describeError(err) {
    if (err instanceof ApiError) {
        if (err.status === 403)
            return 'You do not have permission to perform this action.';
        if (err.status === 404)
            return 'The requested resource was not found.';
        if (err.status === 409)
            return err.message || 'A conflict occurred.';
    }
    return 'Something went wrong. Please try again.';
}
export function useTcGroups() {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const groups = useQuery({
        queryKey: ['tc-groups'],
        queryFn: getTcGroups,
    });
    const createGroup = useMutation({
        mutationFn: (data) => createTcGroup(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tc-groups'] }),
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedCreateGroup'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const updateGroup = useMutation({
        mutationFn: ({ groupId, data, }) => updateTcGroup(groupId, data),
        onSuccess: (_updated, vars) => {
            queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
            queryClient.invalidateQueries({ queryKey: ['tc-group-detail', vars.groupId] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedUpdateGroup'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    // Retire a group. Every listing keys on the caller's now-inactive membership, so
    // the group vanishes from Settings, the TC navigation and the worklist together.
    const deleteGroup = useMutation({
        mutationFn: (groupId) => deleteTcGroup(groupId),
        onSuccess: (_void, groupId) => {
            queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
            queryClient.invalidateQueries({ queryKey: ['tc-group-detail', groupId] });
            queryClient.invalidateQueries({ queryKey: ['consultant-profile'] });
            toast({ title: t('settings.tcGroups.mutations.deletedGroup') });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedDeleteGroup'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    return {
        groups,
        createGroup,
        updateGroup,
        deleteGroup,
    };
}
export function useTcGroupDetail(groupId) {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const detail = useQuery({
        queryKey: ['tc-group-detail', groupId],
        queryFn: () => getTcGroupDetail(groupId ?? ''),
        enabled: !!groupId,
    });
    const members = useQuery({
        queryKey: ['tc-group-members', groupId],
        queryFn: () => getTcGroupMembers(groupId ?? ''),
        enabled: !!groupId,
    });
    const memberIds = (members.data ?? []).map((m) => m.userId);
    const pendingQuery = useQuery({
        queryKey: ['tc-group-pending', groupId, memberIds],
        queryFn: () => getPendingMemberIds(memberIds),
        enabled: memberIds.length > 0,
    });
    // Invitations not yet accepted. Only the group's admins may list them; for anyone else the
    // request is refused and the list is simply empty.
    const invitations = useQuery({
        queryKey: ['tc-group-invitations', groupId],
        queryFn: () => getTcGroupInvitations(getActiveRegion() ?? 'us', groupId ?? ''),
        enabled: !!groupId,
        retry: false,
    });
    const provisionMember = useMutation({
        mutationFn: (payload) => provisionTcGroupMember({
            ...payload,
            groupId: groupId ?? '',
            region: getActiveRegion() ?? 'us',
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-group-invitations', groupId] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedSendInvite'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const updateRole = useMutation({
        mutationFn: ({ memberId, role, }) => updateTcGroupMemberRole(groupId ?? '', memberId, role),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-group-members', groupId] });
            queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedUpdateRole'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const removeMember = useMutation({
        mutationFn: (memberId) => removeTcGroupMember(groupId ?? '', memberId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-group-members', groupId] });
            queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedRemoveMember'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const revokeMember = useMutation({
        mutationFn: (email) => revokePendingMember({ email, region: getActiveRegion() ?? 'us', groupId: groupId ?? '' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-group-members', groupId] });
            queryClient.invalidateQueries({ queryKey: ['tc-group-pending', groupId] });
            queryClient.invalidateQueries({ queryKey: ['tc-group-invitations', groupId] });
            queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedRevokeMember'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const resendInvite = useMutation({
        mutationFn: ({ email, role }) => provisionTcGroupMember({
            email,
            role,
            region: getActiveRegion() ?? 'us',
            groupId: groupId ?? '',
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-group-invitations', groupId] });
        },
        onError: (err) => toast({
            title: t('settings.tcGroups.mutations.failedSendInvite'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    return {
        detail,
        members,
        pendingMemberIds: pendingQuery.data ?? [],
        invitations: invitations.data ?? [],
        provisionMember,
        updateRole,
        removeMember,
        revokeMember,
        resendInvite,
    };
}
// The TC equivalent of useEffectiveClinics: the list of TC groups the current
// user can act on, in the *active deployment region*. Super admins (via the JWT
// is_super_admin claim) fan out to every group in the region through the
// claim-aware GET /teleconsultation/groups, presented as role 'admin' so the
// region/routing/billing edit affordances unlock (the backend already permits a
// super admin to write any group). Regular users get their membership groups
// from the consultant profile, role unchanged — their UX is untouched.
//
// Prefer this over reading consultant-profile.groups directly on the super-admin
// settings surfaces: the profile is membership-only and returns [] for a
// membership-less super admin, which would otherwise hide every group.
export function useEffectiveTcGroups() {
    const { isSuperAdmin, isLoading: isUserLoading } = useCurrentUser();
    const { data: profile, isLoading: isProfileLoading } = useQuery({
        queryKey: ['consultant-profile'],
        queryFn: getConsultantProfile,
        staleTime: 5 * 60_000,
        enabled: !isSuperAdmin,
    });
    const { data: allGroups, isLoading: isAllGroupsLoading } = useQuery({
        queryKey: ['tc-groups'],
        queryFn: getTcGroups,
        staleTime: 5 * 60_000,
        enabled: isSuperAdmin,
    });
    const groups = useMemo(() => {
        if (isSuperAdmin) {
            // Synthetic admin role on every region group — backend authorizes via the
            // claim, so this only drives the UI's edit/delete affordances.
            return (allGroups ?? []).map((g) => ({
                id: g.id,
                name: g.name,
                role: 'admin',
                isActive: g.isActive,
            }));
        }
        return (profile?.groups ?? []).map((g) => ({
            id: g.id,
            name: g.name,
            role: g.role,
            isActive: true,
        }));
    }, [isSuperAdmin, allGroups, profile?.groups]);
    return {
        groups,
        isLoading: isUserLoading || (isSuperAdmin ? isAllGroupsLoading : isProfileLoading),
    };
}
// The clinics the current user is allowed to connect to a group. This mirrors the
// backend gate: a regular user may only connect clinics they administer (the
// 'Clinic Admin' role), while a super admin may connect any active clinic. Used by
// both the create-group auto-connect step and the Settings → Clinics connect picker.
export function useConnectableClinics() {
    const { currentUser, isSuperAdmin, isLoading: isUserLoading } = useCurrentUser();
    const { clinics: effective, isLoading: isEffectiveLoading, isError: isEffectiveError, refetch: refetchEffective, } = useEffectiveClinics();
    const clinics = useMemo(() => {
        if (isSuperAdmin) {
            return effective.map((c) => ({
                clinicId: c.clinicId,
                clinicCode: c.clinicCode,
                clinicName: c.clinicName,
            }));
        }
        return (currentUser?.clinicRoles ?? []).flatMap((cr) => cr.roleName === 'Clinic Admin'
            ? [
                {
                    clinicId: cr.clinicId,
                    clinicCode: cr.clinicCode,
                    clinicName: cr.clinicName,
                },
            ]
            : []);
    }, [isSuperAdmin, effective, currentUser]);
    // Loading until currentUser resolves (regular users derive from it directly);
    // super admins additionally wait on the all-clinics fetch. Reporting ready too
    // early would briefly show zero connectable clinics.
    return {
        clinics,
        isLoading: isUserLoading || (isSuperAdmin && isEffectiveLoading),
        isError: isSuperAdmin && isEffectiveError,
        refetch: refetchEffective,
    };
}
export function useClinicConnections(groupId) {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const connections = useQuery({
        queryKey: ['tc-group-clinics', groupId],
        queryFn: () => getConnectedClinics(groupId ?? ''),
        enabled: !!groupId,
    });
    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['tc-group-clinics', groupId] });
        queryClient.invalidateQueries({ queryKey: ['tc-groups'] });
        queryClient.invalidateQueries({ queryKey: ['tc-group-detail', groupId] });
    };
    const connectClinic = useMutation({
        mutationFn: (clinicId) => connectClinicToGroup(groupId ?? '', { clinicId }),
        onSuccess: invalidate,
        onError: (err) => toast({
            title: t('settings.tcGroups.clinics.failedConnect'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const setEnabled = useMutation({
        mutationFn: ({ clinicId, enabled }) => updateClinicConnection(groupId ?? '', clinicId, { enabled }),
        onSuccess: invalidate,
        onError: (err) => toast({
            title: t('settings.tcGroups.clinics.failedUpdate'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    // Group-side connection PATCH for the row-expand editors (SLA terms, the
    // group's default template). Group-admin gated on the server.
    const updateConnection = useMutation({
        mutationFn: ({ clinicId, data }) => updateClinicConnection(groupId ?? '', clinicId, data),
        onSuccess: invalidate,
        onError: (err) => toast({
            title: t('settings.tcGroups.clinics.failedUpdate'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    // Clinic-side counterpart: the receiving/delivery preferences PATCH
    // (clinic-admin gated). A 403 (not this clinic's admin) surfaces through the
    // same failed-update toast via describeError.
    const updatePreferences = useMutation({
        mutationFn: ({ clinicId, prefs }) => patchConnectionPreferences(clinicId, groupId ?? '', prefs),
        onSuccess: invalidate,
        onError: (err) => toast({
            title: t('settings.tcGroups.clinics.failedUpdate'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    const disconnectClinic = useMutation({
        mutationFn: (clinicId) => disconnectClinicFromGroup(groupId ?? '', clinicId),
        onSuccess: invalidate,
        onError: (err) => toast({
            title: t('settings.tcGroups.clinics.failedDisconnect'),
            description: describeError(err),
            variant: 'destructive',
        }),
    });
    return {
        connections,
        connectClinic,
        setEnabled,
        updateConnection,
        updatePreferences,
        disconnectClinic,
    };
}
